import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertTriangle, Trash2 } from 'lucide-react';
import { deleteModelSchema, type DeleteModelFormData } from './schemas';
import { useTritonModelStore } from '@/store/tritonModelStore';

interface DeleteModelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  modelName: string;
  instanceId: string;
  onSuccess?: () => void;
}

const DeleteModelDialog = ({
  open,
  onOpenChange,
  modelName,
  instanceId,
  onSuccess,
}: DeleteModelDialogProps) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const deleteModel = useTritonModelStore((state) => state.deleteModel);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<DeleteModelFormData>({
    resolver: zodResolver(deleteModelSchema),
    defaultValues: {
      confirmName: '',
    },
  });

  const confirmNameValue = watch('confirmName');
  const isNameMatch = confirmNameValue === modelName;

  const onSubmit = async (data: DeleteModelFormData) => {
    if (data.confirmName !== modelName) {
      return;
    }

    setIsDeleting(true);

    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 500));

    // Delete from store
    deleteModel(instanceId, modelName);

    setIsDeleting(false);
    reset();
    onOpenChange(false);
    onSuccess?.();
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      reset();
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="h-5 w-5" />
            Delete Model
          </DialogTitle>
          <DialogDescription className="pt-2">
            This action cannot be undone. This will permanently delete the model{' '}
            <span className="font-mono font-semibold text-foreground">{modelName}</span>{' '}
            from the repository.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p className="text-sm text-red-600">
              Please type <span className="font-mono font-bold">{modelName}</span> to confirm
              deletion.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmName">Model Name</Label>
            <Input
              id="confirmName"
              placeholder={modelName}
              {...register('confirmName')}
              className={errors.confirmName ? 'border-red-500' : ''}
              autoComplete="off"
            />
            {errors.confirmName && (
              <p className="text-sm text-red-500">{errors.confirmName.message}</p>
            )}
            {confirmNameValue && !isNameMatch && (
              <p className="text-sm text-amber-500">Model name does not match</p>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="destructive"
              disabled={!isNameMatch || isDeleting}
            >
              {isDeleting ? (
                <>
                  <Trash2 className="mr-2 h-4 w-4 animate-pulse" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete Model
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteModelDialog;
